import { useState, useEffect, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import type { ChecklistItem, FieldType, ChecklistRepeatFrequency } from '../types/checklist.types';
import { useChecklistById } from '../context/checklist-context';
import { ControlsProvider, ControlCombobox } from '@/components/ui/ControlCombobox';
import { FrameworkCombobox, FrameworksProvider, cleanStandardName } from '@/components/ui/FrameworkCombobox';
import { cleanDateAndTimeObject, toLocalDateString } from '@shared/renderDate';
import { DatePicker } from '@/components/ui/date-picker';
import { MCMMappingsCard } from '@/components/mcm/MCMMappingsCard';
import { useTenancy } from '@/contexts/TenancyContext';

interface ChecklistInstanceEditorProps {
  checklistId: string;
  onSave: (checklist: any) => void;
  onCancel: () => void;
}

export function ChecklistInstanceEditor({ checklistId, onSave, onCancel }: ChecklistInstanceEditorProps) {
  const { checklist, loading } = useChecklistById(checklistId);
  const { activeTenancy } = useTenancy();
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItem, setNewItem] = useState<Partial<ChecklistItem>>({
    title: '',
    control: '',
    fieldType: 'Checkbox',
    requireEvidence: false,
  });
  // ControlCombobox selection state
  const [selectedControls, setSelectedControls] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [standards, setStandards] = useState<string[]>([]);
  const [status, setStatus] = useState('Draft');
  const [repeatFrequency, setRepeatFrequency] = useState<ChecklistRepeatFrequency>('never');
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [reviewDate, setReviewDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isAssuranceTask, setIsAssuranceTask] = useState(false);
  const [createdAt, setCreatedAt] = useState(new Date());
  const frostedFieldClasses = 'bg-background border border-border/60 focus-visible:ring-primary/30 focus-visible:ring-offset-0 hover:border-primary/40 placeholder:text-muted-foreground';
  const sectionCardClasses = 'rounded-xl border border-border bg-card/95 p-6 shadow-md shadow-primary/5';
  const subtleHeadingClasses = 'text-xs font-semibold uppercase tracking-[0.18em] text-primary/70';
  const primaryFramework = standards[0] ?? '';
  const prettyPrimaryFramework = useMemo(() => cleanStandardName(primaryFramework), [primaryFramework]);

  // Sync state from checklist when it loads/changes
  useEffect(() => {
    if (!checklist) return;

    setItems(checklist.items || []);
    setTitle(checklist.title || '');
    setDescription(checklist.description || '');
    const incomingStandards = Array.isArray(checklist.standards)
      ? checklist.standards
      : checklist.standard
        ? [checklist.standard]
        : [];
    setStandards(incomingStandards.length ? incomingStandards : []);
    setStatus(checklist.status || 'Draft');

    const repeat = checklist.repeat;
    const derivedFrequency = repeat?.frequency ?? 'never';
    setRepeatFrequency(derivedFrequency as ChecklistRepeatFrequency);
    setRepeatInterval(repeat?.interval ?? 1);

    const legacy = checklist as any;
    const fallbackReviewDate = cleanDateAndTimeObject(
      checklist.reviewDate ?? legacy?.nextReviewDate ?? legacy?.lastReviewDate ?? null
    ) ?? null;
    setReviewDate(fallbackReviewDate);

    setStartDate(cleanDateAndTimeObject(repeat?.startDate ?? null) ?? null);
    setEndDate(cleanDateAndTimeObject(repeat?.endDate ?? null) ?? null);

    setIsAssuranceTask(checklist.isAssuranceTask || false);
    setCreatedAt(checklist.createdAt ? new Date(checklist.createdAt) : new Date());
  }, [checklist]);

  const isRecurring = repeatFrequency !== 'never';

  useEffect(() => {
    if (isRecurring && !startDate) {
      const base = cleanDateAndTimeObject(reviewDate ?? new Date()) ?? new Date();
      setStartDate(base);
    }
    if (!isRecurring) {
      if (!reviewDate) {
        setReviewDate(cleanDateAndTimeObject(startDate ?? new Date()) ?? new Date());
      }
      setStartDate(null);
      setEndDate(null);
    }
  }, [isRecurring, reviewDate, startDate]);

  useEffect(() => {
    if (!isRecurring && !reviewDate) {
      setReviewDate(cleanDateAndTimeObject(new Date()) ?? new Date());
    }
  }, [isRecurring, reviewDate]);

  const scheduleSummary = useMemo(() => {
    if (!isRecurring || !startDate) return null;
    const intervalLabel = repeatInterval === 1 ? 'every' : `every ${repeatInterval}`;
    const unit = repeatFrequency === 'daily'
      ? repeatInterval === 1 ? 'day' : 'days'
      : repeatFrequency === 'weekly'
        ? repeatInterval === 1 ? 'week' : 'weeks'
        : repeatFrequency === 'monthly'
          ? repeatInterval === 1 ? 'month' : 'months'
            : repeatInterval === 1 ? 'year' : 'years';
    const endText = endDate ? ` until ${toLocalDateString(endDate)}` : '';
    return `${intervalLabel} ${unit} starting ${toLocalDateString(startDate)}${endText}`;
  }, [isRecurring, startDate, endDate, repeatInterval, repeatFrequency]);

  // Derive control IDs from all items (control + controlOptions)
  const checklistControlIds = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const it of items) {
      if (it?.control) set.add(String(it.control));
      if (Array.isArray(it?.controlOptions)) it.controlOptions.map(String).forEach(id => id && set.add(id));
    }
    return Array.from(set);
  }, [items]);

  // Default entity link for this checklist instance
  const defaultEntityLink = useMemo(() => {
    const id = String(checklist?.id || checklistId);
    const titleText = String((checklist as any)?.title || title || 'Checklist');
    return {
      id: `internal:checklist:${id}`,
      ref: id,
      title: titleText,
      domain: 'Checklist',
      relation: 'verifies',
    };
  }, [checklist?.id, checklistId, title]);

  // Ensure selected master control is reflected in at least one item
  const ensureControlLinked = useCallback(async (cid: string) => {
    setItems(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      // already present somewhere
      if (prev.some(it => (Array.isArray(it.controlOptions) && it.controlOptions.includes(cid)) || it.control === cid)) return prev;
      const idx = 0;
      const target = prev[idx];
      const nextOptions = Array.from(new Set([...(target.controlOptions || []), cid]));
      const next = [...prev];
      next[idx] = { ...target, controlOptions: nextOptions, control: target.control || cid };
      return next;
    });
  }, []);

  const handleAddItem = () => {
    if (!newItem.title?.trim()) return;
    const item: ChecklistItem = {
      id: `item-${uuidv4()}`,
      title: newItem.title.trim(),
      control: (selectedControls && selectedControls.length > 0 ? selectedControls[0] : (newItem.control || '')),
      controlOptions: (selectedControls && selectedControls.length > 0 ? selectedControls : (newItem.control ? [newItem.control] : [])),
      fieldType: newItem.fieldType || 'Checkbox',
      requireEvidence: newItem.requireEvidence || false,
      order: items.length,
    };
    setItems([...items, item]);
    setNewItem({
      title: '',
      control: '',
      fieldType: 'Checkbox',
      requireEvidence: false,
    });
    setSelectedControls([]);
    setIsAddingItem(false);
  };

  const handleRemoveItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reorderedItems = Array.from(items);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);
    const updatedItems = reorderedItems.map((item, index) => ({ ...item, order: index }));
    setItems(updatedItems);
  };

  const updateItem = useCallback(
    (id: string, updates: Partial<ChecklistItem> | ((item: ChecklistItem) => Partial<ChecklistItem>)) => {
      setItems(prev => prev.map(item => {
        if (item.id !== id) return item;
        const patch = typeof updates === 'function' ? updates(item) : updates;
        return { ...item, ...patch };
      }));
    },
    []
  );

  if (loading || !checklist) {
    return (
      <div className="rounded-xl border border-border bg-card/90 p-10 text-center shadow-lg">
        <div className="mx-auto flex w-full max-w-xs flex-col items-center gap-4">
          <span className="h-10 w-10 animate-spin rounded-full border-2 border-primary/40 border-t-transparent" />
          <div>
            <p className="text-sm font-medium text-primary/80">Loading checklist</p>
            <p className="text-sm text-muted-foreground">Preparing your editor…</p>
          </div>
        </div>
      </div>
    );
  }

  const handleSave = () => {
    const resolvedStart = isRecurring
      ? cleanDateAndTimeObject(startDate ?? reviewDate ?? new Date()) ?? cleanDateAndTimeObject(new Date()) ?? new Date()
      : null;
    const resolvedEnd = isRecurring
      ? cleanDateAndTimeObject(endDate ?? null) ?? null
      : null;

    const repeatConfig = isRecurring
      ? {
          frequency: repeatFrequency,
          interval: repeatInterval,
          startDate: resolvedStart,
          endDate: resolvedEnd,
        }
      : undefined;

    const resolvedReviewDate = isRecurring
      ? resolvedStart
      : cleanDateAndTimeObject(reviewDate ?? null) ?? null;

    onSave({
      ...checklist,
      title,
      description,
      standard: primaryFramework,
      standards: Array.from(new Set(standards)).filter(Boolean).length ? Array.from(new Set(standards)).filter(Boolean) : [],
      status,
      repeat: repeatConfig,
      reviewDate: resolvedReviewDate,
      isAssuranceTask,
      createdAt,
      items,
      updatedAt: new Date(),
    });
  };


  return (
    <FrameworksProvider>
      <div className="rounded-2xl border border-border bg-card/95 shadow-xl">
        <div className="border-b border-border/70 px-8 py-6">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="space-y-2">
              <span className={subtleHeadingClasses}>Checklist Builder</span>
              <h2 className="text-2xl font-semibold text-foreground">
                {title || checklist.title || 'Untitled checklist'}
              </h2>
              <p className="max-w-xl text-sm text-muted-foreground">
                A refreshed, calmer presentation that hints at our frosted design while staying close to the current admin look and feel.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3 text-sm md:items-end">
              <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">
                {status}
              </Badge>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="border-border bg-muted text-foreground">
                  {standards.length > 1 ? `${standards.length} frameworks` : prettyPrimaryFramework}
                </Badge>
                <Badge variant="secondary" className="border-border bg-muted text-foreground">
                  {items.length} item{items.length === 1 ? '' : 's'}
                </Badge>
              </div>
              <span className="text-xs text-muted-foreground">Created {createdAt.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="space-y-8 p-8">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.4fr,1fr]">
            <div className={sectionCardClasses}>
              <div className="space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80">
                      Checklist Title
                    </label>
                    <Input
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder="Enter checklist title"
                      className={`${frostedFieldClasses} h-12 rounded-xl`}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80">
                      Framework
                    </label>
                    <FrameworkCombobox
                      selected={standards}
                      setSelected={(values) => {
                        const unique = Array.from(new Set(values.filter(Boolean)));
                        setStandards(unique.length ? unique : []);
                      }}
                      placeholder="Select frameworks"
                      className={`${frostedFieldClasses} min-h-[3rem] rounded-lg text-left`}
                      fullWidth
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80">
                    Description
                  </label>
                  <Textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Share guidance, expectations, or context for this checklist"
                    className={`${frostedFieldClasses} min-h-[130px] rounded-lg px-4 py-3`}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className={sectionCardClasses}>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <span className={subtleHeadingClasses}>Recurrence & Reviews</span>
                    <p className="text-xs text-muted-foreground">
                      Configure an optional cadence so this checklist appears in calendars and dashboards.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
                      <Select
                        value={repeatFrequency}
                        onValueChange={value => {
                          const typed = value as ChecklistRepeatFrequency;
                          setRepeatFrequency(typed);
                          if (typed === 'never') {
                            setStartDate(null);
                            setEndDate(null);
                          } else {
                            const base = cleanDateAndTimeObject(startDate ?? reviewDate ?? new Date()) ?? new Date();
                            setStartDate(base);
                            if (!reviewDate) {
                              setReviewDate(base);
                            }
                          }
                        }}
                      >
                        <SelectTrigger className={`${frostedFieldClasses} h-10 w-[200px] rounded-lg`}>
                          <SelectValue placeholder="Does not repeat" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="never">Does not repeat</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="annually">Annually</SelectItem>
                        </SelectContent>
                      </Select>
                      {isRecurring && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Every</span>
                          <Input
                            type="number"
                            min={1}
                            value={repeatInterval}
                            onChange={e => {
                              const numeric = Number(e.target.value);
                              setRepeatInterval(Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : 1);
                            }}
                            className={`${frostedFieldClasses} h-10 w-[80px] rounded-lg text-center`}
                          />
                          <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            {repeatFrequency === 'daily'
                              ? 'day(s)'
                              : repeatFrequency === 'weekly'
                                ? 'week(s)'
                                : repeatFrequency === 'monthly'
                                  ? 'month(s)'
                                  : 'year(s)'}
                          </span>
                        </div>
                      )}
                    </div>
                    {scheduleSummary && (
                      <p className="text-xs text-muted-foreground">
                        {scheduleSummary}
                      </p>
                    )}
                  </div>

                  {!isRecurring ? (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80" htmlFor="checklist-review-date">
                        Review date
                      </label>
                      <DatePicker
                        date={reviewDate ?? undefined}
                        setDate={date => setReviewDate(date ?? null)}
                        className={`${frostedFieldClasses} h-11 rounded-lg`}
                        showPreview={false}
                      />
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80" htmlFor="checklist-start-date">
                          Start date
                        </label>
                        <DatePicker
                          date={startDate ?? undefined}
                          setDate={date => setStartDate(date ?? null)}
                          className={`${frostedFieldClasses} h-11 rounded-lg`}
                          showPreview={false}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80" htmlFor="checklist-end-date">
                          End date (optional)
                        </label>
                        <DatePicker
                          date={endDate ?? undefined}
                          setDate={date => setEndDate(date ?? null)}
                          className={`${frostedFieldClasses} h-11 rounded-lg`}
                          showPreview={false}
                        />
                        <p className="text-xs text-muted-foreground">
                          Leave blank to continue indefinitely.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={`${sectionCardClasses} space-y-6`}>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <span className={subtleHeadingClasses}>Checklist Items</span>
                  <h3 className="text-xl font-semibold text-foreground">Design each step with intention</h3>
                  <p className="text-sm text-muted-foreground">
                    Drag to reorder, fine-tune controls, and capture evidence requirements for each check.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddingItem(true)}
                  className="border-primary/30 bg-primary/10 text-primary hover:bg-primary/20"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Item
                </Button>
              </div>

              {isAddingItem && (
                <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-xs font-medium uppercase tracking-[0.18em] text-primary/90">
                        Item Title
                      </label>
                      <Input
                        value={newItem.title || ''}
                        onChange={e => setNewItem({ ...newItem, title: e.target.value })}
                        placeholder="What should be verified?"
                        className={`${frostedFieldClasses} h-11 rounded-lg`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-medium uppercase tracking-[0.18em] text-primary/90">
                        Field Type
                      </label>
                      <Select value={newItem.fieldType} onValueChange={(value: FieldType) => setNewItem({ ...newItem, fieldType: value })}>
                        <SelectTrigger className={`${frostedFieldClasses} h-11 rounded-lg`} aria-label="Select field type">
                          <SelectValue placeholder="Select field type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Checkbox">Checkbox</SelectItem>
                          <SelectItem value="Text">Text</SelectItem>
                          <SelectItem value="Number">Number</SelectItem>
                          <SelectItem value="Date">Date</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start">
                    <div className="flex-1 space-y-2">
                      <label className="text-xs font-medium uppercase tracking-[0.18em] text-primary/90">
                        Control Reference
                      </label>
                      <div className={`${frostedFieldClasses} rounded-lg border border-transparent p-1`}
                        data-testid="new-item-control-combobox"
                      >
                        <ControlsProvider>
                          <ControlCombobox
                            selected={selectedControls}
                            setSelected={vals => setSelectedControls(vals.map(v => String(v)))}
                            placeholder="Search and select controls..."
                          />
                        </ControlsProvider>
                      </div>
                    </div>
                    <div className="flex w-full items-center justify-between rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 lg:w-auto lg:flex-col lg:items-start lg:gap-2">
                      <div>
                        <p className="text-sm font-medium text-primary">Require evidence</p>
                        <p className="text-xs text-primary/80">Collect supporting files or notes.</p>
                      </div>
                      <Switch
                        id="requireEvidence"
                        checked={Boolean(newItem.requireEvidence)}
                        onCheckedChange={checked => setNewItem({ ...newItem, requireEvidence: checked })}
                        className="data-[state=checked]:bg-primary"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setIsAddingItem(false)} className="sm:min-w-[120px]">
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAddItem}
                      disabled={!newItem.title?.trim()}
                      className="sm:min-w-[140px]"
                    >
                      Add Item
                    </Button>
                  </div>
                </div>
              )}

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="checklist-items">
                  {provided => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                      {items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border/70 bg-muted/30 py-12 text-center text-sm text-muted-foreground">
                          No items yet — click “New Item” to sculpt your first step.
                        </div>
                      ) : (
                        items.map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  width: (provided.draggableProps.style as any)?.width ?? '100%',
                                  margin: 0,
                                }}
                                className={`group relative rounded-lg border bg-card/95 p-5 shadow-sm transition-all duration-200 hover:border-primary/40 hover:shadow-md ${snapshot.isDragging ? 'border-primary/40 shadow-lg shadow-primary/30 bg-primary/10' : 'border-border'
                                  }`}
                              >
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="flex h-10 w-10 items-center justify-center rounded-full border border-border/60 bg-muted/40 text-muted-foreground transition-colors duration-200 group-hover:border-primary/40 group-hover:text-primary"
                                  >
                                    <GripVertical className="h-5 w-5" />
                                  </div>
                                  <div className="flex-1 space-y-4">
                                    <div className="grid gap-4 md:grid-cols-2">
                                      <div className="space-y-2">
                                        <label className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80">
                                          Item Title
                                        </label>
                                        <Input
                                          value={item.title}
                                          onChange={e => updateItem(item.id, { title: e.target.value })}
                                          placeholder="What should be verified?"
                                          className={`${frostedFieldClasses} h-11 rounded-lg`}
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <label className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80">
                                          Field Type
                                        </label>
                                        <Select value={item.fieldType} onValueChange={(value: FieldType) => updateItem(item.id, { fieldType: value })}>
                                          <SelectTrigger className={`${frostedFieldClasses} h-11 rounded-lg`} aria-label="Select field type">
                                            <SelectValue placeholder="Select field type" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="Checkbox">Checkbox</SelectItem>
                                            <SelectItem value="Text">Text</SelectItem>
                                            <SelectItem value="Number">Number</SelectItem>
                                            <SelectItem value="Date">Date</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>

                                    <div className="space-y-2">
                                      <label className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground/80">
                                        Control Options
                                      </label>
                                      <div className={`${frostedFieldClasses} rounded-lg border border-transparent p-1`}
                                        data-testid={`item-${item.id}-control-combobox`}
                                      >
                                        <ControlsProvider>
                                          <ControlCombobox
                                            dontShowSelectedControls
                                            selected={item.controlOptions || []}
                                            setSelected={vals => {
                                              const controlOptions = vals.map(v => String(v));
                                              updateItem(item.id, current => ({
                                                controlOptions,
                                                control: controlOptions[0] || current.control,
                                              }));
                                            }}
                                            placeholder="Search and select controls..."
                                          />
                                        </ControlsProvider>
                                      </div>
                                    </div>

                                    <div className="flex w-full items-center justify-between rounded-lg border border-border/70 bg-muted/20 px-4 py-3 lg:w-auto lg:gap-2">
                                      <div>
                                        <p className="text-sm font-medium text-foreground">Require evidence</p>
                                        <p className="text-xs text-muted-foreground">Collect supporting files or notes.</p>
                                      </div>
                                      <Switch
                                        id={`requireEvidence-${item.id}`}
                                        checked={Boolean(item.requireEvidence)}
                                        onCheckedChange={checked => updateItem(item.id, { requireEvidence: checked })}
                                        className="data-[state=checked]:bg-primary"
                                      />
                                    </div>
                                  </div>

                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="self-start text-destructive transition hover:bg-destructive/10 hover:text-destructive"
                                    onClick={() => handleRemoveItem(item.id)}
                                  >
                                    <Trash2 className="h-5 w-5" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-border/70 bg-muted/30 px-8 py-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Save your progress to publish this experience to your team.
          </p>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <Button type="button" variant="ghost" onClick={onCancel} className="border border-transparent hover:border-border/70">
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} className="shadow-md shadow-primary/25">
              Save Checklist
            </Button>
          </div>
        </div>

        {/* MCM Mappings for this Checklist */}
        <div className="p-8 pt-0">
          <MCMMappingsCard
            tenancyId={activeTenancy?.id}
            controlIds={checklistControlIds}
            defaultEntityLink={defaultEntityLink}
            onEnsureControlLinked={ensureControlLinked}
            className="max-w-none"
          />
        </div>
      </div>
    </FrameworksProvider>
  );
}
